import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Library, Lock, Pencil, Plus, Receipt, Scroll, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Concepto, UnidadDeCobro } from '@/shared/api/tipos';
import { PestanasDeCatalogos } from '@/modules/catalogos/PestanasDeCatalogos';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { Interruptor } from '@/shared/ui/Interruptor';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { notificar } from '@/shared/ui/avisos';

const UNIDADES: { codigo: UnidadDeCobro; nombre: string }[] = [
  { codigo: 'mes', nombre: 'Mes de servicio' },
  { codigo: 'kg', nombre: 'Kilogramo' },
  { codigo: 'tonelada', nombre: 'Tonelada' },
  { codigo: 'm2', nombre: 'Metro cuadrado' },
  { codigo: 'm_lineal', nombre: 'Metro lineal' },
  { codigo: 'km', nombre: 'Kilómetro' },
];

export function PaginaConceptos() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<Concepto | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<Concepto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const conceptos = useQuery({
    queryKey: ['conceptos'],
    queryFn: () => api.get<Concepto[]>('/conceptos'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['conceptos'] });
  }

  function alFallar(causa: unknown, respaldo: string) {
    const mensaje = causa instanceof ErrorDeApi ? causa.message : respaldo;
    setError(mensaje);
    notificar.error(mensaje);
  }

  const cambiarEstado = useMutation({
    mutationFn: (c: Concepto) =>
      api.put<Concepto>(`/conceptos/${c.id}`, {
        codigo: c.codigo,
        nombre: c.nombre,
        baseLegal: c.baseLegal,
        unidad: c.unidad,
        tarifaIva: c.tarifaIva,
        activo: !c.activo,
        orden: c.orden,
      }),
    onSuccess: refrescar,
    onError: (causa) => alFallar(causa, 'No se pudo cambiar el estado'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/conceptos/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Concepto eliminado');
    },
    onError: (causa) => alFallar(causa, 'No se pudo eliminar el concepto'),
  });

  return (
    <>
      <EncabezadoDePagina
        icono={Library}
        titulo="Catálogos"
        subtitulo="Los vocabularios que alimenta EPAGAL"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo concepto"
            aria-label="Nuevo concepto"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <PestanasDeCatalogos />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Receipt className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">La descripción es lo que el ciudadano lee.</span> Va tal
          cual al comprobante electrónico y al SRI. El código es el del servicio que presta EPAGAL
          —no el del predio ni el del cliente— y no se puede cambiar una vez emitido.
        </p>
      </div>

      {conceptos.isPending ? (
        <Cargando titulo="Cargando conceptos" detalle="Obteniendo registros" alto="h-48" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <div className="hidden items-center gap-4 border-b border-armazon-100 bg-armazon-50/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 lg:flex">
            <span className="w-32 shrink-0">Código SRI</span>
            <span className="min-w-0 flex-[2] basis-72">Descripción en la factura</span>
            <span className="w-28 shrink-0">Se cobra por</span>
            <span className="w-16 shrink-0 text-right">IVA</span>
            <span className="w-24 shrink-0 text-center">Estado</span>
            <span className="w-16 shrink-0" aria-hidden />
          </div>

          <div className="divide-y divide-armazon-100">
            {(conceptos.data ?? []).map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60"
              >
                <div className="w-32 shrink-0">
                  <p className="importe truncate text-xs font-semibold text-armazon-700">
                    {c.codigo}
                  </p>
                  {c.codigoPrincipal && (
                    <p
                      className="importe truncate text-[10px] text-armazon-500"
                      title="Código principal con el que sale en el comprobante"
                    >
                      EN EL RIDE: {c.codigoPrincipal}
                    </p>
                  )}

                  {c.esTasaMensual && (
                    <span
                      className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-marca-600"
                      title="Es el concepto con el que se emite la tasa mensual del catastro"
                    >
                      <Lock className="size-2.5 shrink-0" aria-hidden />
                      Emisión mensual
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-[2] basis-72">
                  <p className="truncate text-sm font-medium uppercase text-armazon-800">
                    {c.nombre}
                  </p>
                  <span
                    className="mt-0.5 flex items-center gap-1 truncate text-[10px] uppercase text-armazon-400"
                    title={c.baseLegal}
                  >
                    <Scroll className="size-2.5 shrink-0" aria-hidden />
                    {c.baseLegal}
                  </span>
                </div>

                <p className="w-28 shrink-0 text-xs uppercase text-armazon-600">{c.unidadNombre}</p>

                <p
                  className={`importe w-16 shrink-0 text-right text-sm ${
                    c.grabaIva ? 'font-bold text-indigo-600' : 'text-armazon-400'
                  }`}
                >
                  {c.tarifaIva} %
                </p>

                <div className="flex w-24 shrink-0 justify-center">
                  <Interruptor
                    activo={c.activo}
                    alCambiar={() => cambiarEstado.mutate(c)}
                    etiqueta={`Activar o desactivar ${c.codigo}`}
                    deshabilitado={cambiarEstado.isPending}
                  />
                </div>

                <div className="flex w-16 shrink-0 items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => setEditando(c)}
                    title="Editar"
                    aria-label={`Editar ${c.codigo}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="size-[15px]" aria-hidden />
                  </button>

                  {!c.esTasaMensual && (
                    <button
                      type="button"
                      onClick={() => setAEliminar(c)}
                      title="Eliminar"
                      aria-label={`Eliminar ${c.codigo}`}
                      className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                    >
                      <Trash2 className="size-[15px]" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogoDeConcepto
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        concepto={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        titulo="Eliminar concepto"
        descripcion={aEliminar === null ? '' : `Se eliminará «${aEliminar.codigo}» del catálogo.`}
        alCerrar={() => setAEliminar(null)}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function DialogoDeConcepto({
  abierto,
  concepto,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  concepto: Concepto | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = concepto !== null;

  const [codigo, setCodigo] = useState(concepto?.codigo ?? '');
  const [nombre, setNombre] = useState(concepto?.nombre ?? '');
  const [baseLegal, setBaseLegal] = useState(concepto?.baseLegal ?? '');
  const [unidad, setUnidad] = useState<UnidadDeCobro>(concepto?.unidad ?? 'mes');
  const [iva, setIva] = useState(concepto === null ? '0' : String(concepto.tarifaIva));
  const [orden, setOrden] = useState(concepto === null ? '0' : String(concepto.orden));
  const [codigoPrincipal, setCodigoPrincipal] = useState(concepto?.codigoPrincipal ?? '');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = codigo.trim() !== '' && nombre.trim() !== '' && baseLegal.trim() !== '';

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const cuerpo = {
        codigo: codigo.trim(),
        nombre: nombre.trim(),
        baseLegal: baseLegal.trim(),
        unidad,
        tarifaIva: Number(iva) || 0,
        activo: concepto?.activo ?? true,
        orden: Number(orden) || 0,
        codigoPrincipal: codigoPrincipal.trim() || null,
      };

      if (editando) {
        await api.put<Concepto>(`/conceptos/${concepto.id}`, cuerpo);
      } else {
        await api.post<Concepto>('/conceptos', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Concepto ${editando ? 'actualizado' : 'creado'}: ${cuerpo.codigo}`);
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el concepto';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar concepto' : 'Nuevo concepto facturable'}
      descripcion={editando ? concepto.codigo : 'Un servicio que EPAGAL cobra'}
      icono={Receipt}
      alEnviar={enviar}
      enviando={enviando || !listo}
    >
      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <CampoDelPanel
        etiqueta="Descripción en la factura"
        requerido
        ayuda="ES LO QUE EL CIUDADANO LEE EN SU COMPROBANTE Y LO QUE SE DECLARA AL SRI"
      >
        <textarea
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          rows={3}
          className={`${claseEntradaPanel} h-auto resize-none py-2 leading-relaxed`}
          placeholder="ART. 9 SERVICIO DE GESTIÓN INTEGRAL DE RESIDUOS SÓLIDOS COMUNES"
          autoFocus
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Base legal"
        requerido
        ayuda="EL ARTÍCULO Y LITERAL QUE LO CREA. SIN NORMA DETRÁS NO SE DEFIENDE EN UNA AUDITORÍA."
      >
        <input
          value={baseLegal}
          onChange={(e) => setBaseLegal(e.target.value)}
          className={claseEntradaPanel}
          placeholder="Art. 9 literal a) — Ordenanza GIRS Latacunga"
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Código del servicio"
        requerido
        ayuda={
          editando
            ? 'NO SE PUEDE CAMBIAR: ES LO QUE SE DECLARÓ AL SRI EN LOS COMPROBANTES YA EMITIDOS'
            : 'VA EN EL CAMPO codigoPrincipal DEL XML. ES EL CÓDIGO DEL SERVICIO, NO EL DEL PREDIO.'
        }
      >
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          className={`${claseEntradaPanel} importe disabled:bg-armazon-50 disabled:text-armazon-500`}
          placeholder="GIRS-ART9-A"
          disabled={editando}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Código principal en el comprobante"
        ayuda="LA COLUMNA «COD. PRINCIPAL» DEL RIDE. VACÍO, SALE EL CÓDIGO DEL SERVICIO. LOS DE GENERADORES VAN CON LA PARTIDA 3.8.01.01.02."
      >
        <input
          value={codigoPrincipal}
          onChange={(e) => setCodigoPrincipal(e.target.value)}
          maxLength={25}
          className={`${claseEntradaPanel} importe`}
          placeholder="3.8.01.01.02"
        />
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Se cobra por" requerido ayuda="LA UNIDAD DE MEDIDA DE LA LÍNEA">
        <select
          value={unidad}
          onChange={(e) => setUnidad(e.target.value as UnidadDeCobro)}
          className={claseEntradaPanel}
        >
          {UNIDADES.map((u) => (
            <option key={u.codigo} value={u.codigo}>
              {u.nombre.toUpperCase()}
            </option>
          ))}
        </select>
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel etiqueta="IVA %" ayuda="LA TASA GIRS VA AL 0 %">
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={iva}
            onChange={(e) => setIva(e.target.value)}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Orden" ayuda="EN QUÉ POSICIÓN SE LISTA">
          <input
            type="number"
            min="0"
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>
      </DosColumnas>
    </PanelLateral>
  );
}
